import { chain } from '@/config/chain'
import { usePurchaseOrder } from '@/hooks/usePurchaseOrder'
import { usePurchaseQuote } from '@/hooks/usePurchaseQuote'
import { API_BASE_URL } from '@/services/api'
import { formatEthDisplay, formatWithCommas } from '@/utils/format'
import { computeRequiredFeeWithMargin, formatFeeDisplay } from '@/utils/gasEstimation'
import { useWallet } from '@components/providers/WalletProvider'
import { ArcsTokenIcon } from '@components/ui/ArcsTokenIcon'
import { fetchAuthSession } from 'aws-amplify/auth'
import type React from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { type Address, formatEther } from 'viem'
import { useBalance } from 'wagmi'
import { PurchaseModal } from './PurchaseModal'

/** pm_getPaymasterStubData 用の仮 UserOp */
const STUB_USER_OP_GAS = {
  callGasLimit: '0x186a0',
  verificationGasLimit: '0x7A120',
  preVerificationGas: '0x5208',
  maxFeePerGas: '0x174876e800', // 100 gwei
  maxPriorityFeePerGas: '0x174876e800',
} as const

// キーボード表示時のビューポート追従用フック
const useKeyboardAwareBottom = () => {
  const [bottomOffset, setBottomOffset] = useState(0)

  useLayoutEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const handleResize = () => {
      // visualViewportの高さとwindow内の高さの差がキーボードの高さ
      const keyboardHeight = window.innerHeight - viewport.height
      setBottomOffset(keyboardHeight > 0 ? keyboardHeight : 0)
    }

    viewport.addEventListener('resize', handleResize)
    viewport.addEventListener('scroll', handleResize)

    return () => {
      viewport.removeEventListener('resize', handleResize)
      viewport.removeEventListener('scroll', handleResize)
    }
  }, [])

  return bottomOffset
}

// 見積もりは常に1 ARXで取得され、レート表示用として使用される
// 実際の購入量はconfirm時に指定

// ドーナツプログレスバー（有効期限カウントダウン）
const ExpirationProgress: React.FC<{
  expiresAt: string
  totalSeconds: number
  onExpired?: () => void
}> = ({ expiresAt, totalSeconds, onExpired }) => {
  const canvasRef = useRef<SVGCircleElement>(null)
  const hasCalledExpiredRef = useRef(false)
  const rafIdRef = useRef<number>(0)
  const onExpiredRef = useRef(onExpired)

  // 定数
  const radius = 6
  const circumference = 2 * Math.PI * radius
  const fillAnimationDuration = 300 // 円が閉じるアニメーションの時間（ms）

  // onExpiredの参照を更新
  useEffect(() => {
    onExpiredRef.current = onExpired
  }, [onExpired])

  useEffect(() => {
    hasCalledExpiredRef.current = false

    const expiresAtMs = new Date(expiresAt).getTime()
    const totalMs = totalSeconds * 1000
    const startMs = expiresAtMs - totalMs
    const prefetchThresholdMs = 500
    const mountTime = Date.now()
    // マウント時の初期プログレス値を記録
    const initialProgress = Math.max(0, Math.min(1, 1 - (mountTime - startMs) / totalMs))

    const animate = () => {
      const now = Date.now()
      const timeSinceMount = now - mountTime
      const elapsed = now - startMs
      const remainingMs = expiresAtMs - now

      // 期限前にリフェッチを開始
      if (remainingMs <= prefetchThresholdMs && !hasCalledExpiredRef.current) {
        hasCalledExpiredRef.current = true
        onExpiredRef.current?.()
      }

      // 現在のプログレス（0〜1）
      const currentProgress = Math.max(0, Math.min(1, 1 - elapsed / totalMs))

      let displayProgress: number
      if (timeSinceMount < fillAnimationDuration) {
        // 開始時の円が閉じるアニメーション（0から初期値へイージング）
        const t = timeSinceMount / fillAnimationDuration
        const eased = 1 - (1 - t) ** 3
        displayProgress = eased * initialProgress
      } else {
        // 通常の線形カウントダウン
        displayProgress = currentProgress
      }

      // SVGを直接更新（React再レンダリングを回避）
      if (canvasRef.current) {
        canvasRef.current.style.strokeDashoffset = String(circumference * (1 - displayProgress))
        // 残り25%以下で赤色に変更
        canvasRef.current.style.stroke = currentProgress <= 0.25 ? '#EF4444' : '#22C55E'
      }

      // 期限後1秒まで継続
      if (remainingMs > -1000) {
        rafIdRef.current = requestAnimationFrame(animate)
      }
    }

    rafIdRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
    }
  }, [expiresAt, totalSeconds, circumference])

  return (
    <div className="size-4">
      <svg viewBox="0 0 16 16" className="w-full h-full -rotate-90" aria-hidden="true">
        {/* 背景の円 */}
        <circle cx="8" cy="8" r={radius} stroke="#E4E4E7" strokeWidth="2" fill="none" />
        {/* プログレスの円 */}
        <circle
          ref={canvasRef}
          cx="8"
          cy="8"
          r={radius}
          stroke="#22C55E"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
        />
      </svg>
    </div>
  )
}

// 警告アイコン（オレンジ）
const WarningIcon: React.FC = () => (
  <div className="size-8 flex-shrink-0">
    <svg viewBox="0 0 32 32" fill="none" className="w-full h-full" aria-hidden="true">
      <circle cx="16" cy="16" r="14" stroke="#F97316" strokeWidth="2" />
      <path d="M16 10V18" stroke="#F97316" strokeWidth="2" strokeLinecap="round" />
      <circle cx="16" cy="22" r="1.5" fill="#F97316" />
    </svg>
  </div>
)

// ETHアイコンコンポーネント
const EthIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-black rounded-full flex items-center justify-center ${className}`}>
    <svg viewBox="0 0 24 24" fill="none" className="w-[60%] h-[70%]" aria-hidden="true">
      <path d="M12 2L6 12L12 16L18 12L12 2Z" fill="white" />
      <path d="M12 16L6 12L12 22L18 12L12 16Z" fill="white" fillOpacity="0.7" />
    </svg>
  </div>
)

// ローディングスピナー
const LoadingSpinner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`relative ${className}`}>
    <svg className="animate-spin" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="7" stroke="#E4E4E7" strokeWidth="2" />
      <path
        d="M15 8C15 4.13401 11.866 1 8 1"
        stroke="url(#gradient)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <defs>
        <linearGradient id="gradient" x1="8" y1="1" x2="15" y2="8" gradientUnits="userSpaceOnUse">
          <stop stopColor="#3B82F6" />
          <stop offset="1" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
    </svg>
  </div>
)

type ModalStep = 'confirm' | 'processing' | 'success'

export const PurchaseForm: React.FC = () => {
  const { smartWalletAddress } = useWallet()
  const [arxAmountInput, setArxAmountInput] = useState('')
  const [estimatedFeeWei, setEstimatedFeeWei] = useState<bigint | null>(null)
  const [isEstimatingFee, setIsEstimatingFee] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalStep, setModalStep] = useState<ModalStep>('confirm')
  const [purchasedAmount, setPurchasedAmount] = useState('')
  const isInitializedRef = useRef(false)
  const bottomOffset = useKeyboardAwareBottom()

  const {
    quote,
    config,
    isLoading: isQuoteLoading,
    error: quoteError,
    isQuoteValid,
    fetchQuote,
    refetchQuote,
  } = usePurchaseQuote()

  const feeMarginPercent = (() => {
    const raw = import.meta.env.VITE_PAYMASTER_FEE_MARGIN_PERCENT
    const n = raw != null ? Number.parseInt(raw, 10) : 50
    return Number.isNaN(n) || n < 0 ? 50 : n
  })()

  // ページ読み込み時にレート表示用の見積もりを取得（常に1 ARX）
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      console.log('[PurchaseForm] Fetching initial quote...')
      fetchQuote()
    }
  }, [fetchQuote])

  // ガス代見積もり（Transfer.tsx と同じパターン）
  useEffect(() => {
    let cancelled = false
    setEstimatedFeeWei(null)

    const estimateFee = async () => {
      if (!smartWalletAddress || !arxAmountInput || Number(arxAmountInput) <= 0) {
        if (!cancelled) setEstimatedFeeWei(null)
        return
      }

      if (cancelled) return
      setIsEstimatingFee(true)

      try {
        const paymasterApiUrl = `${API_BASE_URL}/paymaster/rpc`
        const stubUserOp = {
          sender: smartWalletAddress as Address,
          nonce: '0x0',
          callData: '0x',
          ...STUB_USER_OP_GAS,
        }
        const session = await fetchAuthSession()
        if (cancelled) return
        const authToken = session.tokens?.idToken?.toString()

        const response = await fetch(paymasterApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: 1,
            method: 'pm_getPaymasterStubData',
            params: [
              stubUserOp,
              '0x0000000071727De22E5E9d8BAf0edAc6f37da032',
              `0x${chain.id.toString(16)}`,
              { payWith: 'eth', authToken },
            ],
          }),
        })
        const data = await response.json()
        if (cancelled) return
        const totalFeeEthWeiHex = data.result?.context?.totalFeeEthWei as string | undefined

        if (totalFeeEthWeiHex) {
          const cleanHex = totalFeeEthWeiHex.startsWith('0x')
            ? totalFeeEthWeiHex
            : `0x${totalFeeEthWeiHex}`
          if (!cancelled) setEstimatedFeeWei(BigInt(cleanHex))
        } else {
          // フォールバック: ETH転送の概算
          if (!cancelled) setEstimatedFeeWei(21000n * 20000000000n)
        }
      } catch (err) {
        console.error('Fee estimation error:', err)
        if (!cancelled) setEstimatedFeeWei(21000n * 20000000000n)
      } finally {
        if (!cancelled) setIsEstimatingFee(false)
      }
    }

    estimateFee()

    return () => {
      cancelled = true
    }
  }, [arxAmountInput, smartWalletAddress])

  const {
    status: purchaseStatus,
    error: purchaseError,
    executePurchase,
    reset: resetPurchase,
  } = usePurchaseOrder()

  // ETH残高取得
  const { data: ethBalance } = useBalance({
    address: smartWalletAddress as Address,
    chainId: chain.id,
    query: {
      staleTime: 0,
      refetchOnMount: 'always' as const,
    },
  })

  // 購入処理ステータスの監視
  useEffect(() => {
    if (purchaseStatus === 'completed') {
      setPurchasedAmount(arxAmountInput)
      setModalStep('success')
    } else if (purchaseStatus === 'error') {
      // エラー時は処理中ステップのまま（エラー表示）
    } else if (purchaseStatus !== 'idle') {
      setModalStep('processing')
    }
  }, [purchaseStatus, arxAmountInput])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // 数値のみ許可
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setArxAmountInput(value)
    }
  }, [])

  // ユーザー入力に基づく計算値
  const inputAmount = Number.parseFloat(arxAmountInput) || 0
  // 1 ARXあたりのETHレート（見積もりから取得）
  const ethRatePerArx = quote ? Number(quote.ethAmount) : 0
  // ユーザー入力に対する必要ETH
  const calculatedEth = ethRatePerArx * inputAmount

  // マージン付き手数料（wei）
  const requiredFeeWeiWithMargin = computeRequiredFeeWithMargin({
    estimatedFeeWei,
    feeToken: 'ethereum',
    paymasterConstantFeeWei: 0n,
    feeMarginPercent,
  })
  const displayFeeText = formatFeeDisplay(requiredFeeWeiWithMargin)
  const feeEth = Number(requiredFeeWeiWithMargin) / 1e18

  // ARX送金手数料（ETH）
  const arxTransferGasEth = quote ? Number(quote.arxTransferGasEth) : 0

  // 残高不足チェック（購入額 + ARX送金手数料 + ネットワーク手数料）
  const isInsufficientBalance =
    calculatedEth > 0 &&
    ethBalance &&
    calculatedEth + arxTransferGasEth + feeEth > Number(formatEther(ethBalance.value))

  const handlePurchaseClick = useCallback(() => {
    if (!isQuoteValid || !quote) return
    setModalStep('confirm')
    setIsModalOpen(true)
  }, [isQuoteValid, quote])

  const handleConfirm = useCallback(async () => {
    if (!quote) return
    setModalStep('processing')
    await executePurchase(quote, arxAmountInput, requiredFeeWeiWithMargin)
  }, [quote, executePurchase, arxAmountInput, requiredFeeWeiWithMargin])

  const handleCloseModal = useCallback(() => {
    // 処理中は閉じられない（エラー時は閉じられる）
    if (modalStep === 'processing' && purchaseStatus !== 'error') return

    setIsModalOpen(false)
    setModalStep('confirm')

    // 成功またはエラー後はリセット
    if (modalStep === 'success' || purchaseStatus === 'error') {
      setArxAmountInput('')
      resetPurchase()
      refetchQuote()
    }
  }, [modalStep, purchaseStatus, resetPurchase, refetchQuote])

  // 販売一時停止中かどうか
  const isSalesPaused = config ? Number(config.maxArxPurchaseAmount) <= 0 : false

  // 最小・最大購入数量チェック
  // Note: maxAmountは販売一時停止中に0になる。isAboveMaximumのmaxAmount > 0ガードで誤判定を防いでいる
  const minAmount = config ? Number(config.minArxPurchaseAmount) : 0
  const maxAmount = config ? Number(config.maxArxPurchaseAmount) : 0
  const isBelowMinimum = inputAmount > 0 && minAmount > 0 && inputAmount < minAmount
  const isAboveMaximum = inputAmount > 0 && maxAmount > 0 && inputAmount > maxAmount

  // 購入ボタンの有効/無効
  const isPurchaseDisabled =
    isSalesPaused ||
    !isQuoteValid ||
    isQuoteLoading ||
    isInsufficientBalance ||
    isBelowMinimum ||
    isAboveMaximum ||
    !arxAmountInput ||
    inputAmount <= 0

  const ethBalanceFormatted = ethBalance
    ? formatEthDisplay(Number(formatEther(ethBalance.value)))
    : '0'

  return (
    <>
      {/* メインコンテンツエリア */}
      <div className="flex flex-col gap-4 pb-40">
        {/* メインカード */}
        <div className="bg-[#f0f0f1] rounded-2xl">
          {/* 上部カード：入力エリア */}
          <div className="bg-white border border-[#dfdfe0] rounded-2xl p-4 shadow-[0px_1px_0.5px_0.05px_rgba(24,24,27,0.04)]">
            {/* ヘッダー行 */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ArcsTokenIcon className="size-10" />
                <span className="text-base font-medium text-[#1f1f1f]">ARCS</span>
              </div>
              <div className="flex items-start gap-1">
                <div className="py-1">
                  {isQuoteLoading ? (
                    <LoadingSpinner className="size-4" />
                  ) : quote && config ? (
                    <ExpirationProgress
                      key={quote.quoteId}
                      expiresAt={quote.expiresAt}
                      totalSeconds={config.quoteExpirationSeconds}
                      onExpired={() => refetchQuote()}
                    />
                  ) : (
                    <div className="size-4 rounded-full border-2 border-[#E4E4E7]" />
                  )}
                </div>
                <div className="text-right">
                  <div className="text-base font-medium text-[#1f1f1f]">
                    {quote ? `${formatEthDisplay(Number(quote.ethAmount))}ETH` : '0ETH'}
                  </div>
                  <div className="text-base font-medium text-[#9f9f9f]">
                    {quote
                      ? `¥${Number(quote.totalJpy).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '¥0.00'}
                  </div>
                </div>
              </div>
            </div>

            {/* 金額入力 */}
            <div className="py-1">
              <input
                type="text"
                inputMode="decimal"
                value={arxAmountInput}
                onChange={handleInputChange}
                placeholder="0"
                className="w-full text-right text-5xl font-medium text-[#1f1f1f] bg-transparent border-none outline-none placeholder:text-[#1f1f1f]"
              />
            </div>
          </div>

          {/* 下部：ETH変換表示 */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <EthIcon className="size-6" />
              <span className="text-base font-medium text-[#1f1f1f]">ETH</span>
            </div>
            <span className="text-base font-medium text-[#1f1f1f]">
              {formatEthDisplay(calculatedEth)}
            </span>
          </div>
        </div>

        {/* 手数料内訳セクション */}
        {inputAmount > 0 && (
          <div className="flex flex-col gap-2 p-3 border border-zinc-200 rounded-2xl bg-zinc-50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">購入額</span>
              <span className="text-sm font-medium text-zinc-900">
                {formatWithCommas(formatEthDisplay(calculatedEth))} ETH
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">推定購入手数料</span>
              <span className="text-sm font-medium text-zinc-900">
                {isEstimatingFee
                  ? '計算中...'
                  : `${formatWithCommas(formatEthDisplay(arxTransferGasEth + feeEth))} ETH`}
              </span>
            </div>
            {!isEstimatingFee && requiredFeeWeiWithMargin > 0n && (
              <>
                <div className="border-t border-zinc-200" />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-zinc-700">合計</span>
                  <span className="text-sm font-semibold text-zinc-900">
                    {formatWithCommas(formatEthDisplay(calculatedEth + arxTransferGasEth + feeEth))}{' '}
                    ETH
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* 販売一時停止中バナー */}
        {isSalesPaused && (
          <div className="bg-[#fef2f2] rounded-2xl p-4 flex items-center gap-4">
            <div className="size-8 flex-shrink-0">
              <svg viewBox="0 0 32 32" fill="none" className="w-full h-full" aria-hidden="true">
                <circle cx="16" cy="16" r="14" stroke="#EF4444" strokeWidth="2" />
                <path d="M16 10V18" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                <circle cx="16" cy="22" r="1.5" fill="#EF4444" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#18181b]">
                現在ARCSの販売を一時停止しています
              </p>
              <p className="text-xs font-medium text-[#71717a]">
                しばらく時間をおいてから再度お試しください
              </p>
            </div>
          </div>
        )}

        {/* 購入上限・下限 */}
        {!isSalesPaused && config && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-sm font-medium text-[#9f9f9f]">
              <span>※ 最小購入数量</span>
              <span>{Number(config.minArxPurchaseAmount).toLocaleString()}ARX</span>
            </div>
            <div className="flex items-center justify-between text-sm font-medium text-[#9f9f9f]">
              <span>※ 最大購入数量</span>
              <span>{Number(config.maxArxPurchaseAmount).toLocaleString()}ARX</span>
            </div>
          </div>
        )}

        {/* 最小購入数量未満警告 */}
        {isBelowMinimum && (
          <div className="bg-[#fff7ed] rounded-2xl p-4 flex items-center gap-4">
            <WarningIcon />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#18181b]">最小購入数量を下回っています</p>
              <p className="text-xs font-medium text-[#71717a]">
                最小購入数量は{minAmount.toLocaleString()}ARXです
              </p>
            </div>
          </div>
        )}

        {/* 最大購入数量超過警告 */}
        {isAboveMaximum && (
          <div className="bg-[#fff7ed] rounded-2xl p-4 flex items-center gap-4">
            <WarningIcon />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#18181b]">最大購入数量を超えています</p>
              <p className="text-xs font-medium text-[#71717a]">
                最大購入数量は{maxAmount.toLocaleString()}ARXです
              </p>
            </div>
          </div>
        )}

        {/* 残高不足警告 */}
        {isInsufficientBalance && (
          <div className="bg-[#fff7ed] rounded-2xl p-4 flex items-center gap-4">
            <WarningIcon />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#18181b]">残高が不足しています</p>
              <p className="text-xs font-medium text-[#71717a]">
                購入には、残高が{formatEthDisplay(calculatedEth + arxTransferGasEth + feeEth)}
                ETH以上必要です（手数料含む）
              </p>
            </div>
          </div>
        )}

        {/* エラー表示 */}
        {quoteError && (
          <div className="bg-red-50 rounded-2xl p-4">
            <p className="text-sm text-red-600">{quoteError}</p>
          </div>
        )}
      </div>

      {/* 固定ボトムエリア（キーボード追従） */}
      <div
        className="fixed left-0 right-0 flex justify-center bg-white"
        style={{
          bottom: bottomOffset,
          paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex flex-col gap-4 w-full max-w-md px-4 pt-4">
          {/* 残高カード */}
          <div className="bg-[#fafafa] rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="size-6" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect
                  x="3"
                  y="6"
                  width="18"
                  height="14"
                  rx="2"
                  stroke="#71717a"
                  strokeWidth="1.5"
                />
                <path d="M3 10H21" stroke="#71717a" strokeWidth="1.5" />
                <rect x="14" y="13" width="4" height="3" rx="0.5" fill="#71717a" />
              </svg>
              <span className="text-sm font-medium text-[#71717a]">残高</span>
            </div>
            <span className="text-base font-semibold text-[#18181b]">{ethBalanceFormatted}ETH</span>
          </div>

          {/* 購入ボタン */}
          <button
            type="button"
            onClick={handlePurchaseClick}
            disabled={isPurchaseDisabled}
            className={`
              w-full py-3 px-5 rounded-xl text-base font-semibold transition-colors
              ${
                isPurchaseDisabled
                  ? 'bg-[#f0f0f1] text-white cursor-not-allowed'
                  : 'bg-[#3b82f6] border border-[#2563eb] text-white shadow-[0px_0px_0px_1px_#d6f2fe,0px_0px_0px_4px_#f3fbff,0px_1px_1px_0.5px_rgba(24,24,27,0.05)] hover:bg-[#2563eb]'
              }
            `}
          >
            ETHで購入
          </button>
        </div>
      </div>

      {/* 統一購入モーダル */}
      <PurchaseModal
        isOpen={isModalOpen}
        step={modalStep}
        arxAmount={modalStep === 'success' ? purchasedAmount : arxAmountInput}
        ethAmount={calculatedEth}
        arxTransferGasEth={arxTransferGasEth}
        estimatedFee={isEstimatingFee ? '計算中...' : `${displayFeeText} ETH`}
        estimatedFeeEth={feeEth}
        purchaseStatus={purchaseStatus}
        error={purchaseError}
        onConfirm={handleConfirm}
        onClose={handleCloseModal}
      />
    </>
  )
}
